import React, { useCallback, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { Box, Text } from '@common-fe/common-fe';

import routes from '@/common/routes';
import { useIsAddressUndeliverable } from '@/modules/core/components/BadAddressBanner';
import { useShowLockBanner } from '@/modules/core/hooks';
import useEmployerTerminationBanner from '@/modules/core/hooks/useEmployerTerminationBanner';
import { useOrganizationProperties } from '@/modules/transaction/hooks';
import { useShowLimitedAccessTopBanner } from '@/modules/user/components/LimitedAccess/hooks';
import whiteLabelingConfig, { THEMES } from '@/utils/whiteLabeling/whiteLabelingConfig';

import { useHelpMenuItemList, useMenuItemList } from '../hooks';
import Logo from '../Logo';

import BackgroundImage from './BackgroundImage';
import { Content, Wrapper } from './Sidebar.styles';
import SidebarOption from './SidebarOption';
const isGustoTheme = THEMES.gusto === whiteLabelingConfig.title;
interface Props {
  minified?: boolean;
  isMobileMode?: boolean;
  children?: React.ReactNode;
}
const Sidebar: React.FC<Props> = ({ minified, isMobileMode, children }) => {
  const [expanded, setExpanded] = useState(false);
  const { pathname } = useLocation();
  const { formattedList } = useMenuItemList();
  const isLockBannerShowed = useShowLockBanner();
  const isLimitedAccessTopBanner = useShowLimitedAccessTopBanner();
  const { isUndeliverable: isBadAddressBannerShowed } = useIsAddressUndeliverable();
  const { bannerHeight } = useEmployerTerminationBanner();
  const { organizationProperties } = useOrganizationProperties();
  const { formattedList: helpList } = useHelpMenuItemList({ organizationProperties });

  const history = useHistory();

  const handleSetTab = useCallback(
    (url?: string) => {
      if (url && pathname !== url) history.push(url);
    },
    [history, pathname]
  );
  const handleClickLogo = useCallback(() => {
    history.push(routes.HOME);
  }, [history]);

  if (isMobileMode) {
    return (
      <Box direction="column">
        <Box direction="row" justify="between" pad={{ horizontal: 'm' }}>
          <Logo minify={false} horizontalMode onClick={handleClickLogo} />
          {children}
        </Box>
        <Box direction="row" justify="between" pad={{ horizontal: 'm' }} margin={{ top: 'm' }}>
          {formattedList.map((item) => (
            <SidebarOption
              testId={item.key}
              isMobileMode
              path={pathname}
              minify={true}
              key={item.key}
              currentKey={item.key}
              title={item.title}
              icon={item.icon}
              options={item.options}
              delimiter={item.delimiter}
              url={item.url}
              onPick={handleSetTab}
              link={item.link}
              disabled={item.disabled}
              hintComponent={item.hintComponent}
              dropdownOptions={item?.dropdownOptions}
            />
          ))}
        </Box>
      </Box>
    );
  }

  return (
    <Wrapper minify={!!minified}>
      <Content
        data-testid="SideBar-wrapper"
        isLockBannerMode={isLockBannerShowed || isLimitedAccessTopBanner || isBadAddressBannerShowed}
        customTopValue={bannerHeight}
        minify={!!minified && !expanded}
        onMouseEnter={() => minified && setExpanded(true)}
        onMouseLeave={() => minified && setExpanded(false)}
      >
        <Logo minify={!!minified && !expanded} onClick={handleClickLogo} />
        <Box overflow="auto" fill>
          {formattedList.map((item) => (
            <SidebarOption
              path={pathname}
              minify={!!minified && !expanded}
              key={item.key}
              currentKey={item.key}
              title={item.title}
              icon={item.icon}
              options={item.options}
              delimiter={item.delimiter}
              url={item.url}
              onPick={handleSetTab}
              link={item.link}
              disabled={item.disabled}
              hintComponent={item.hintComponent}
              wrapperStyle={item.wrapperStyle}
            />
          ))}

          <Box margin={{ top: 'auto', bottom: 'spacing20' }}>
            {!!helpList.length && (
              <>
                {helpList.map((item) => (
                  <SidebarOption
                    path={pathname}
                    minify={!!minified && !expanded}
                    key={item.key}
                    currentKey={item.key}
                    title={item.title}
                    icon={item.icon}
                    url={item.url}
                    onPick={handleSetTab}
                    link={item.link}
                    isNotClickable={item.isNotClickable}
                    activeBackgroundOnHover={item.activeBackgroundOnHover}
                    additionalIcon={item.additionalIcon}
                    textStyle={item?.textStyle}
                    withTip={item?.withTip}
                    node={item?.node}
                    hintComponent={item.hintComponent}
                  />
                ))}
              </>
            )}
            {isGustoTheme && !minified && (
              <Text size="small" color="sidebarDelimiter" margin={{ left: 'l', top: 'm' }}>
                Powered by Elevate
              </Text>
            )}
          </Box>

          {(!minified || expanded) && <BackgroundImage />}
        </Box>
      </Content>
    </Wrapper>
  );
};

export default Sidebar;
